import { Link } from "@tanstack/react-router";
import { MapPin, Navigation, Route as RouteIcon, X } from "lucide-react";
import type { MapItem } from "@/lib/map-search";
import { StatusBadge } from "./StatusBadge";
import { formatDistance } from "@/lib/geo";
import type { Origin } from "@/lib/geo";
import { CompareButton } from "./CompareButton";
import { directionsUrl, isPlottable } from "@/lib/google-maps";

/**
 * Bottom-sheet list card shown when a map pin is tapped:
 * photo, title, price, distance, property type, Directions and View Details.
 */
export function ListingBottomCard({
  item,
  origin,
  onClose,
  onViewDetails,
}: {
  item: MapItem;
  /** Current GPS/city origin, used as the Directions starting point. */
  origin?: Origin | null;
  onClose?: () => void;
  onViewDetails?: () => void;
}) {
  const canRoute = isPlottable(item.lat, item.lng);

  return (
    <div className="flex gap-3 p-3">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="h-24 w-24 shrink-0 rounded-2xl object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <StatusBadge purpose={item.purpose} />
              <span className="truncate text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {item.typeLabel}
              </span>
            </div>
            <h3 className="mt-1 truncate text-sm font-bold text-brown-dark">{item.title}</h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-brown-dark hover:bg-brand-soft"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{item.location || "—"}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <Navigation className="h-3.5 w-3.5" /> {formatDistance(item.distanceKm)}
          </span>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="text-base font-bold text-coral">{item.priceLabel}</div>
          <div className="flex items-center gap-2">
            <CompareButton id={item.id} />
            {canRoute && (
              <a
                href={directionsUrl({ lat: item.lat as number, lng: item.lng as number }, origin)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get directions in Google Maps"
                className="flex items-center gap-1 rounded-xl bg-secondary px-3 py-2 text-xs font-semibold text-brown-dark shadow-soft transition hover:bg-brand-soft"
              >
                <RouteIcon className="h-3.5 w-3.5" /> Directions
              </a>
            )}

            {item.kind === "parking" ? (
              <button
                type="button"
                onClick={onViewDetails}
                className="rounded-xl bg-parking px-3.5 py-2 text-xs font-semibold text-white shadow-soft transition hover:brightness-105"
              >
                View Details
              </button>
            ) : (
              <Link
                to="/property/$id"
                params={{ id: item.id }}
                className="rounded-xl bg-brand px-3.5 py-2 text-xs font-semibold text-brown-dark shadow-soft transition hover:brightness-105"
              >
                View Details
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
